import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../Layout";
import { auth } from "../../firebaseConfig";
import { AdminLoadingBar, AdminLoadingStyles, AdminSpinner } from "./AdminLoadingUi";

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
};

const containerStyle = { maxWidth: 1600, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = { border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, background: "#fff" };
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db" };
const searchStyle = { ...inputStyle, minWidth: 320 };
const btnStyle = { padding: "9px 12px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" };
const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = { textAlign: "left", padding: 10, background: COLORS.bgHead, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "normal" };
const tdStyle = { padding: 10, borderBottom: `1px solid ${COLORS.border2}`, verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word" };
const filterInputStyle = { width: "100%", minWidth: 0, padding: "6px 7px", borderRadius: 8, border: "1px solid #d1d5db" };
const filterDateStyle = { ...filterInputStyle, padding: "4px 6px" };

const DATE_COLUMNS = new Set(["date", "service_start_date", "service_end_date"]);

const COLUMN_WIDTHS = {
  buyer_address: 240,
  pay_address: 240,
  consignee_address: 240,
  seller_address_1: 240,
  seller_address_2: 240,
  org_organisation_name: 200,
  product_name: 200,
  category_name_and_quadrant: 220,
  service_description: 220,
  service_unit_price_description: 220,
  service_qty_description: 220,
};

function getColumnWidth(key) {
  return COLUMN_WIDTHS[key] || 150;
}

async function adminGetJson(path) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  const token = await user.getIdToken();
  const res = await fetch(path, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `Request failed: ${res.status}`);
  }
  return await res.json();
}

function clamp(n, lo, hi) {
  const x = Number.isFinite(n) ? n : lo;
  return Math.max(lo, Math.min(hi, x));
}

function buildQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s) return;
    usp.set(k, s);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function splitTerms(raw = "") {
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function escapeRegExp(str = "") {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function uniqueTerms(terms = []) {
  const seen = new Set();
  const out = [];
  for (const t of terms) {
    const v = String(t || "").trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

function HighlightText({ value, terms }) {
  const raw = value == null ? "" : String(value);
  if (!raw.trim()) return <>-</>;

  const clean = uniqueTerms(terms);
  if (!clean.length) return <>{raw}</>;

  const pattern = clean.map(escapeRegExp).join("|");
  if (!pattern) return <>{raw}</>;

  let regex;
  try {
    regex = new RegExp(`(${pattern})`, "ig");
  } catch {
    return <>{raw}</>;
  }

  const parts = raw.split(regex);
  return (
    <>
      {parts.map((part, idx) => {
        const isMatch = clean.some((t) => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <mark key={idx} style={{ backgroundColor: "#fef08a", padding: "0 1px", borderRadius: 3 }}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

function SortHeader({ col, activeField, activeOrder, onSort, loading }) {
  const active = activeField === col.key;
  const arrow = active ? (activeOrder === "ASC" ? " \u25B2" : " \u25BC") : " \u2195";
  return (
    <th
      style={{
        ...thStyle,
        cursor: loading ? "default" : "pointer",
        opacity: loading ? 0.7 : 1,
        fontWeight: active ? 800 : 700,
        width: getColumnWidth(col.key),
        minWidth: getColumnWidth(col.key),
        maxWidth: getColumnWidth(col.key),
      }}
      onClick={() => !loading && onSort(col.key)}
      title={loading ? "" : "Click to sort"}
    >
      {col.label}
      <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span>
    </th>
  );
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>
        Prev
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, width: 90, minWidth: 90 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
        <AdminSpinner loading={loading} />
      </div>
      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>
        Next
      </button>
    </div>
  );
}

export default function AdminGemCleanedContractsDashboardPage() {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [filters, setFilters] = useState({});
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [uniqueContracts, setUniqueContracts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = useDebouncedValue(searchInput.trim(), 350);
  const debouncedFilters = useDebouncedValue(filters, 350);
  const searchHighlightTerms = useMemo(() => splitTerms(searchInput), [searchInput]);
  const highlightTermsByColumn = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      map[col.key] = uniqueTerms([...searchHighlightTerms, ...splitTerms(filters[col.key] || "")]);
    });
    return map;
  }, [columns, searchHighlightTerms, filters]);

  const query = useMemo(() => {
    const q = {
      page,
      limit,
      search,
      sortField,
      sortOrder: sortOrder.toLowerCase(),
    };
    columns.forEach((col) => {
      if (DATE_COLUMNS.has(col.key)) {
        // Date columns use From/To range filters
        const from = debouncedFilters[`${col.key}_from`];
        const to = debouncedFilters[`${col.key}_to`];
        if (from) q[`${col.key}From`] = from;
        if (to) q[`${col.key}To`] = to;
      } else {
        const v = debouncedFilters[col.key];
        if (v != null && String(v).trim()) q[`f_${col.key}`] = String(v).trim();
      }
    });
    return buildQuery(q);
  }, [page, limit, search, sortField, sortOrder, columns, debouncedFilters]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const json = await adminGetJson(`/api/admin/gem-cleaned-contracts-dashboard${query}`);
        if (!mounted) return;
        const cols = Array.isArray(json?.columns) ? json.columns : [];
        setColumns(cols);
        setRows(Array.isArray(json?.rows) ? json.rows : []);
        setTotalRows(Number(json?.totalRows || 0));
        setUniqueContracts(Number(json?.uniqueContracts || 0));
        setTotalPages(Math.max(1, Number(json?.totalPages || 1)));
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [query]);

  function onSort(field) {
    if (sortField === field) {
      setSortOrder((s) => (s === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function clearAllFilters() {
    setSearchInput("");
    setFilters({});
    setPage(1);
  }

  return (
    <Layout>
      <div style={containerStyle}>
        <AdminLoadingStyles />

        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Link to="/admin" style={{ color: "#64748b", textDecoration: "none", fontSize: 14 }}>
                  {"←"} Admin Panel
                </Link>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
                <span>GeM Contracts Full Dashboard</span>
                <AdminSpinner loading={loading} />
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 800 }}>{uniqueContracts.toLocaleString()} contracts</div>
              <div style={smallMuted}>{totalRows.toLocaleString()} rows</div>
            </div>
          </div>

          <AdminLoadingBar loading={loading} />

          <div style={{ marginTop: 12, ...rowStyle }}>
            <input
              style={searchStyle}
              placeholder="Search across all columns (comma-separated terms)"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
            />

            <select
              style={inputStyle}
              value={limit}
              onChange={(e) => {
                setLimit(clamp(parseInt(e.target.value || "50", 10), 1, 200));
                setPage(1);
              }}
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={200}>200 / page</option>
            </select>

            <button style={btnStyle} onClick={clearAllFilters}>
              Clear Filters
            </button>

            <div style={{ marginLeft: "auto" }}>
              <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
            </div>
          </div>

          {error ? <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>Error: {error}</div> : null}
        </div>

        <div style={tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <SortHeader
                    key={col.key}
                    col={col}
                    activeField={sortField}
                    activeOrder={sortOrder}
                    onSort={onSort}
                    loading={loading}
                  />
                ))}
              </tr>
              <tr>
                {columns.map((col) => {
                  const baseTh = { ...thStyle, background: "#fff", padding: 6, width: getColumnWidth(col.key), minWidth: getColumnWidth(col.key), maxWidth: getColumnWidth(col.key) };

                  if (DATE_COLUMNS.has(col.key)) {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <input
                            type="date"
                            style={filterDateStyle}
                            value={filters[`${col.key}_from`] || ""}
                            onChange={(e) => setFilter(`${col.key}_from`, e.target.value)}
                            title="From date"
                          />
                          <input
                            type="date"
                            style={filterDateStyle}
                            value={filters[`${col.key}_to`] || ""}
                            onChange={(e) => setFilter(`${col.key}_to`, e.target.value)}
                            title="To date"
                          />
                        </div>
                      </th>
                    );
                  }

                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <input
                        style={filterInputStyle}
                        placeholder="Filter"
                        value={filters[col.key] || ""}
                        onChange={(e) => setFilter(col.key, e.target.value)}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(1, columns.length)} style={{ ...tdStyle, color: COLORS.muted }}>
                    No rows found for current filters.
                  </td>
                </tr>
              ) : null}

              {rows.map((r, idx) => (
                <tr key={`${r.row_hash || idx}`}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ ...tdStyle, width: getColumnWidth(col.key), minWidth: getColumnWidth(col.key), maxWidth: getColumnWidth(col.key) }}
                      title={r[col.key] == null ? "" : String(r[col.key])}
                    >
                      <HighlightText value={r[col.key]} terms={highlightTermsByColumn[col.key] || searchHighlightTerms} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
        </div>
      </div>
    </Layout>
  );
}
